import { useEffect, type RefObject } from 'react';

/**
 * Updates scroll surface via data attributes — zero React re-renders on scroll.
 * Mobile perf: avoids backdrop-filter toggling through React state.
 */
export function useNavbarScroll(
  navRef: RefObject<HTMLElement | null>,
  pathname: string,
): void {
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const isHome = pathname === '/';
    let scrolled = false;
    let ticking = false;

    const apply = () => {
      ticking = false;
      const y = window.scrollY;
      const threshold = isHome ? 50 : nav.offsetHeight * 0.7;
      const nextScrolled = y > threshold;

      if (nextScrolled !== scrolled) {
        scrolled = nextScrolled;
        nav.dataset.scrolled = nextScrolled ? '1' : '0';
      }

      nav.dataset.hero = isHome && !scrolled ? '1' : '0';
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    apply();
    // Second frame primes scroll styles before the user scrolls (cold compositor).
    const primeId = requestAnimationFrame(apply);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(primeId);
      window.removeEventListener('scroll', onScroll);
    };
  }, [navRef, pathname]);
}
